import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Tooltip,
  Link,
} from '@mui/material';
import { Payment as PaymentIconMui, Settings as SettingsIcon } from '@mui/icons-material';
import PaymentIcon from '../ui/PaymentIcon';
import { usePaymentsAll } from '../../hooks/queries/usePayments';
import { usePlacePayments } from '../../hooks/queries/usePlacePayments';
import PlacePaymentsManagerDialog from '../places/PlacePaymentsManagerDialog';
import placeServicePaymentsService from '../../services/placeServicePaymentsService';

/**
 * PlaceServicePaymentDialog
 *
 * Dialog para configurar formas de pagamento aceitas por um serviço específico no local.
 *
 * Features:
 * - Lista todas as formas de pagamento disponíveis
 * - Permite marcar/desmarcar quais são aceitas
 * - Permite definir valor customizado por forma de pagamento
 * - Valor padrão herdado do PlaceService
 */
export default function PlaceServicePaymentDialog({
  open,
  onClose,
  placeService,
  onUpdate
}) {
  const { data: allPayments = [], isLoading: loadingAllPayments } = usePaymentsAll({
    enabled: open && !!placeService,
  });
  const { data: placePayments = [], isLoading: loadingPlacePayments } = usePlacePayments(
    placeService?.placeId,
    { enabled: open && !!placeService }
  );

  const availablePayments = useMemo(
    () => placePayments
      .map(pp => allPayments.find(p => p.id === pp.paymentId))
      .filter(Boolean)
      .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999)),
    [placePayments, allPayments]
  );

  const loading = loadingAllPayments || loadingPlacePayments;
  const [selectedPayments, setSelectedPayments] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [managerOpen, setManagerOpen] = useState(false);

  useEffect(() => {
    if (open && placeService && availablePayments.length > 0) {
      loadExistingPayments();
    }
  }, [open, placeService, availablePayments]);

  const loadExistingPayments = async () => {
    try {
      setError(null);

      const existingPayments = await placeServicePaymentsService.getByPlaceAndService(
        placeService.placeId,
        placeService.serviceId || placeService.id
      );

      const paymentsState = {};
      availablePayments.forEach(payment => {
        const existing = existingPayments.find(ep => ep.paymentId === payment.id);
        paymentsState[payment.id] = {
          id: existing?.id || null,
          paymentId: payment.id,
          name: payment.name,
          isSelected: !!existing,
          defaultPrice: existing?.defaultPrice ?? null,
          originalPrice: placeService.price || 0
        };
      });

      setSelectedPayments(paymentsState);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar formas de pagamento');
    }
  };

  const handleTogglePayment = (paymentId) => {
    setSelectedPayments(prev => ({
      ...prev,
      [paymentId]: {
        ...prev[paymentId],
        isSelected: !prev[paymentId].isSelected
      }
    }));
  };

  const handlePriceChange = (paymentId, value) => {
    const num = value === '' || value === null ? null : parseFloat(value);
    setSelectedPayments(prev => ({
      ...prev,
      [paymentId]: {
        ...prev[paymentId],
        defaultPrice: num
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      for (const paymentId in selectedPayments) {
        const payment = selectedPayments[paymentId];

        if (payment.isSelected) {
          if (payment.id) {
            await placeServicePaymentsService.update(payment.id, {
              placeId: placeService.placeId,
              serviceId: placeService.serviceId || placeService.id,
              defaultPrice: payment.defaultPrice
            });
          } else {
            await placeServicePaymentsService.create({
              placeId: placeService.placeId,
              serviceId: placeService.serviceId || placeService.id,
              paymentId: payment.paymentId,
              defaultPrice: payment.defaultPrice
            });
          }
        } else {
          if (payment.id) {
            await placeServicePaymentsService.delete(
              placeService.placeId,
              placeService.serviceId || placeService.id,
              payment.id
            );
          }
        }
      }

      if (onUpdate) {
        await onUpdate();
      }

      onClose();
    } catch (err) {
      console.error('Erro ao salvar configurações:', err);
      setError('Erro ao salvar: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <PaymentIconMui color="primary" />
          <Box flex={1} minWidth={0}>
            <Typography variant="h6">
              Formas de Pagamento
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {placeService?.placeName ? (
                <>
                  <strong>{placeService.placeName}</strong>
                  {' · '}
                  {placeService?.service?.name || 'Serviço'}
                </>
              ) : (
                placeService?.service?.name || 'Serviço'
              )}
            </Typography>
          </Box>
          <Tooltip title="Gerenciar formas de pagamento">
            <IconButton
              size="small"
              onClick={() => setManagerOpen(true)}
              sx={{ color: 'text.secondary' }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Só aparecem aqui as formas de pagamento aceitas por este local.{' '}
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  setManagerOpen(true);
                }}
                sx={{ cursor: 'pointer', fontWeight: 500 }}
              >
                Gerenciar pagamentos do local
              </Link>
            </Alert>

            {availablePayments.length === 0 ? (
              <Alert severity="warning">
                Nenhuma forma de pagamento configurada para este local.{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    setManagerOpen(true);
                  }}
                  sx={{ cursor: 'pointer', fontWeight: 500 }}
                >
                  Configure aqui
                </Link>
              </Alert>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    Selecione quais formas de pagamento são aceitas para o serviço{' '}
                    <strong>{placeService?.service?.name || 'este serviço'}</strong>{' '}
                    no local{' '}
                    <strong>{placeService?.placeName || 'este local'}</strong>.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    O valor definido aqui é o <strong>valor padrão</strong> do serviço neste estabelecimento.
                    Ele será utilizado sempre que não houver um valor personalizado configurado para o profissional.
                  </Typography>
                </Alert>

                {availablePayments.map((payment) => {
                  const paymentState = selectedPayments[payment.id] || {};
                  const isSelected = paymentState.isSelected || false;

                  return (
                    <Box key={payment.id} sx={{ mb: 2 }}>
                      <Box display="flex" alignItems="flex-start" gap={2}>
                        <Box sx={{ pt: 0.5 }}>
                          {payment.isGlobal ? (
                            <PaymentIcon displayOrder={payment.displayOrder || 1} size={32} />
                          ) : (
                            <Box
                              sx={{
                                width: 32,
                                height: 21.33,
                                borderRadius: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'primary.main',
                                color: 'white',
                                fontSize: '1rem'
                              }}
                            >
                              <PaymentIconMui sx={{ fontSize: '1rem' }} />
                            </Box>
                          )}
                        </Box>

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleTogglePayment(payment.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1">
                                {payment.name}
                              </Typography>
                              {payment.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {payment.description}
                                </Typography>
                              )}
                            </Box>
                          }
                          sx={{ flex: 1 }}
                        />

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={paymentState.defaultPrice ?? ''}
                            onChange={(e) => handlePriceChange(payment.id, e.target.value)}
                            disabled={!isSelected}
                            placeholder="Deixe vazio para usar o valor padrão do serviço"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                            }}
                            sx={{ width: 120 }}
                            inputProps={{
                              min: 0,
                              step: 0.01,
                              style: { textAlign: 'right' }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Padrão: R$ {(placeService.price || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>

      <PlacePaymentsManagerDialog
        open={managerOpen}
        onClose={() => setManagerOpen(false)}
        place={{ id: placeService?.placeId, name: placeService?.placeName }}
      />
    </Dialog>
  );
}

PlaceServicePaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  placeService: PropTypes.object,
  onUpdate: PropTypes.func,
};
